import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Clock, BookOpen } from "lucide-react";
import { fadeUp, stagger, AnimatedSection } from "@/lib/animations";

export default function ResourcesPage() {
  const resources = [
    {
      id: "1",
      title: "La guida completa ai fondi per il marketing e per le AI per le nonprofit",
      description: "Tutto quello che devi sapere per qualificarti e massimizzare 10.000€/mese in pubblicità gratuita per la tua organizzazione no-profit.",
      category: "Fondi Marketing",
      read_time: "8 min di lettura",
      image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800",
      slug: "guida-fondi-marketing"
    },
    {
      id: "2",
      title: "5 errori del sito web che costano donazioni alla tua nonprofit",
      description: "Errori comuni di design e UX del sito web che allontanano potenziali donatori, e come correggerli velocemente.",
      category: "Web Design",
      read_time: "5 min di lettura",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
      slug: "errori-sito-web-nonprofit"
    },
    {
      id: "3",
      title: "Costruire una comunità online attorno alla tua causa",
      description: "Come creare e coltivare una comunità online fiorente che trasforma i sostenitori occasionali in sostenitori a vita.",
      category: "Comunità",
      read_time: "6 min di lettura",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800",
      slug: "costruire-comunita-online"
    },
    {
      id: "4",
      title: "SEO per le nonprofit: posizionati meglio, raggiungi di più",
      description: "Una roadmap SEO pratica su misura per le organizzazioni no-profit per migliorare la visibilità nelle ricerche e attrarre più sostenitori.",
      category: "SEO",
      read_time: "7 min di lettura",
      image: "https://images.unsplash.com/photo-1432888498266-38ffec3eaf0a?w=800",
      slug: "seo-per-nonprofit"
    },
    {
      id: "5",
      title: "Best practice CRM per le organizzazioni benefiche",
      description: "Come scegliere e implementare il sistema CRM giusto per gestire donatori, volontari e stakeholder in modo efficace.",
      category: "CRM",
      read_time: "6 min di lettura",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800",
      slug: "best-practice-crm-beneficenza"
    },
    {
      id: "6",
      title: "Strategie di raccolta fondi digitale che funzionano davvero",
      description: "Strategie digitali collaudate per potenziare la raccolta fondi online, dalle campagne email ai social media.",
      category: "Strategia",
      read_time: "9 min di lettura",
      image: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800",
      slug: "strategie-raccolta-fondi-digitale"
    }
  ];
  const [filter, setFilter] = useState("Tutti");

  const categories = ["Tutti", ...new Set(resources.map(r => r.category))];
  const filtered = filter === "Tutti" ? resources : resources.filter(r => r.category === filter);

  return (
    <div data-testid="resources-page">
      {/* Hero */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="resources-hero">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center max-w-3xl mx-auto">
            <motion.div variants={fadeUp}>
              <Badge className="bg-[#D85A30]/10 text-[#D85A30] border-[#D85A30]/20 hover:bg-[#D85A30]/15 mb-6 px-4 py-1.5 rounded-full">
                <span className="kicker">Risorse</span>
              </Badge>
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#2C2C2A] leading-[1.1] mb-6">
              Guide e approfondimenti per la{" "}
              <span className="text-[#D85A30]">crescita delle nonprofit</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base md:text-lg text-[#888780] leading-relaxed">
              Risorse gratuite per aiutare la tua organizzazione a massimizzare la presenza digitale, 
              ottenere grants e raggiungere più persone.
            </motion.p>
          </AnimatedSection>
        </div>
      </section>

      {/* Filter + Grid */}
      <section className="py-24 md:py-32 bg-[#F1EFE8]/20" data-testid="resources-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Category Filter */}
          <AnimatedSection className="mb-12">
            <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-2">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setFilter(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    filter === cat
                      ? "bg-[#1D9E75] text-white shadow-md"
                      : "bg-white text-[#888780] border border-[#D9D8D2]/70 hover:bg-[#085041]/5 hover:text-[#2C2C2A]"
                  }`}
                  data-testid={`filter-${cat.toLowerCase()}`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>
          </AnimatedSection>

          {/* Resource Cards */}
          <AnimatedSection className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((resource, i) => (
              <motion.div key={resource.id} variants={fadeUp}>
                <Card className="bg-white border-[#D9D8D2]/50 shadow-[0_10px_30px_-10px_rgba(8,80,65,0.05)] hover:shadow-[0_20px_40px_-10px_rgba(0,0,0,0.1)] transition-all duration-300 h-full group overflow-hidden cursor-pointer" data-testid={`resource-card-${i}`}>
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={resource.image}
                      alt={resource.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <Badge className="bg-white/90 text-[#2C2C2A] border-none text-xs rounded-full backdrop-blur-sm">
                        {resource.category}
                      </Badge>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 text-xs text-[#888780] mb-3">
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {resource.read_time}</span>
                    </div>
                    <h3 className="text-lg font-semibold text-[#2C2C2A] mb-2 leading-snug group-hover:text-[#D85A30] transition-colors">
                      {resource.title}
                    </h3>
                    <p className="text-sm text-[#888780] leading-relaxed mb-4">{resource.description}</p>
                    <span className="inline-flex items-center text-sm font-medium text-[#D85A30] group-hover:gap-2 transition-all">
                      Leggi di Più <ArrowRight className="w-4 h-4 ml-1" />
                    </span>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatedSection>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-[#085041]" data-testid="resources-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection className="max-w-2xl mx-auto">
            <motion.div variants={fadeUp} className="flex items-center justify-center mb-6">
              <BookOpen className="w-12 h-12 text-[#D85A30]" />
            </motion.div>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-semibold text-white leading-tight mb-6">
              Vuoi una strategia personalizzata per la tua nonprofit?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/60 mb-8 text-base">
              La nostra revisione strategica gratuita va più in profondità di qualsiasi guida. Analizziamo la tua situazione specifica.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link to="/book-a-call">
                <Button className="rounded-full px-8 h-12 text-base bg-[#D85A30] hover:bg-[#C04E25] text-white font-medium shadow-lg transition-all hover:-translate-y-1" data-testid="resources-cta-button">
                  Prenota una Chiamata Gratuita
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </motion.div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
